// 停電事故（能源 WP-9b；plan SPEC NRG-K3，設計稿 k04 / k05 / k05b / k05c；Eddy 2026-10-21 WP-0 Q2 錄影影響的依據）：
//   - PowerImpact：事故頁影響區——現況三段（● 停電中（用電池）→ ● 市電已恢復，還有 N 台沒回來 → ↺ 已恢復）、剩餘時間大字 +「約 03:31 用完 · 電量 75%」、
//     最近 30 分鐘電池電量、接在這台 UPS 後面幾台（已離線幾台）、還沒回來的設備〔不用等這台〕、轉電池紀錄；剩餘時間只在跨 15 / 5 分鐘時朗讀
//   - PowerRowLine：事故清單 / 總覽的事故列加一行「UPS 剩約 18 分鐘（負載 62%）」；沒登記下游寫「不知道哪些設備接在 UPS 後面〔登記下游〕」（灰、不准綠）
// 錄影影響只照 kmgr 給的 recording_impact（登記在下游的錄影設備 + 心跳 + 供電路徑由 kmgr 算）；kmgr 沒給、又沒登記下游時寫「不知道」。
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useScope } from '../../../lib/scope';
import { timeStamp } from '../../../lib/timefmt';
import { errText, NS } from '../parts';
import { powerApi } from '../power';
import type { Incident } from '../types';
import { runtimeMin } from './Ups';

const POWER_KINDS = ['power_outage', 'ups_output_off'];

export function isPowerIncident(inc: Pick<Incident, 'kind'>): boolean {
  return POWER_KINDS.includes(inc.kind);
}

function upsHref(home: string, probe: string): string {
  return `/kmgr/network?tab=ups&home=${encodeURIComponent(home)}&ups=${encodeURIComponent(probe)}`;
}

/** 剩餘時間跨門檻（15 / 5 分鐘）才朗讀；大字本身不設 live（每分鐘念會吵）。 */
function useThresholdAnnounce(min: number | undefined): string {
  const { t } = useTranslation(NS);
  const last = useRef<number | null>(null);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (min === undefined) return;
    const level = min < 5 ? 5 : min < 15 ? 15 : 0;
    if (last.current !== null && level && level !== last.current) setMsg(t(level === 5 ? 'power.live_5' : 'power.live_15'));
    last.current = level;
  }, [min, t]);
  return msg;
}

export function PowerImpact({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  const qc = useQueryClient();
  const { homes, planes } = useScope();
  const api = homes.find((h) => h.home_id === inc.home_id)?.endpoints?.api ?? planes[0]?.api ?? '';
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const p = inc.power;
  const min = runtimeMin(p?.ups?.runtime_s);
  const live = useThresholdAnnounce(p?.phase === 'outage' ? min : undefined);
  if (!p) return null;
  const nameOf = (ref: string) => inc.affected?.find((a) => a.target_ref === ref)?.name || ref;
  const ups = p.ups;
  const runout = ups?.runtime_s !== undefined ? timeStamp(Date.now() + ups.runtime_s * 1000, undefined, false) : null;
  const skip = async (ref: string) => {
    setBusy(ref);
    setNote(null);
    try {
      await powerApi.skipWait(api, inc.home_id, inc.incident_id, ref);
      setNote(t('power.skip_done', { name: nameOf(ref) }));
      void qc.invalidateQueries({ queryKey: ['kmgr', 'incident', inc.home_id, inc.incident_id] });
    } catch (e) {
      setNote(errText(t, e));
    } finally {
      setBusy(null);
    }
  };
  const hist = p.battery_history.filter((x) => x.battery_pct !== undefined);
  return (
    <section aria-labelledby="km-pwr-h" data-testid="km-power-impact" data-phase={p.phase}>
      <h2 id="km-pwr-h" className="section-title">
        {t('power.impact_title')}
      </h2>
      <div className={'km-impact-box ' + (p.phase === 'restored' ? 'gap' : 'not_recording')}>
        <div className={'km-pwr-phase ' + p.phase} data-testid="km-power-phase">
          <span aria-hidden>{p.phase === 'restored' ? '↺ ' : '● '}</span>
          {p.phase === 'outage' ? t('power.phase_outage') : p.phase === 'waiting' ? t('power.phase_waiting', { n: p.waiting }) : t('power.phase_restored')}
        </div>
        {p.phase === 'outage' && (
          <>
            <div className="km-pwr-big" data-testid="km-power-runtime">
              {min !== undefined ? t('power.runtime_big', { n: min }) : t('power.runtime_unk')}
            </div>
            <div className="km-sub">
              {[runout ? t('power.runout', { time: runout }) : '', ups?.battery_pct !== undefined ? t('power.battery_pct', { pct: Math.floor(ups.battery_pct) }) : '', ups?.load_pct !== undefined ? t('power.load_pct', { pct: Math.floor(ups.load_pct) }) : '']
                .filter(Boolean)
                .join(' · ')}
            </div>
            <span className="visually-hidden" role="status">
              {live}
            </span>
          </>
        )}
        {p.phase === 'outage' && (
          <div className="km-sub" data-testid="km-power-history" style={{ marginTop: 6 }}>
            {hist.length ? (
              <BatteryLine points={hist.map((x) => ({ at: x.at, v: x.battery_pct! }))} />
            ) : (
              <span className="muted">{t('power.chart_none')}</span>
            )}
          </div>
        )}
        <div style={{ marginTop: 8 }} data-testid="km-power-downstream">
          {p.downstream_total ? (
            p.offline.length ? (
              t('power.downstream_line', { n: p.downstream_total, off: p.offline.length })
            ) : (
              t('power.downstream_line_ok', { n: p.downstream_total })
            )
          ) : (
            <span className="muted">
              {t('power.no_downstream')}{' '}
              {ups && (
                <Link className="btn btn-sm" to={upsHref(inc.home_id, ups.probe_id)}>
                  {t('power.register')}
                </Link>
              )}
            </span>
          )}
        </div>
        {!inc.recording_impact && !p.downstream_total && <div className="km-sub muted">{t('power.no_downstream_rec')}</div>}
      </div>
      {p.offline.length > 0 && p.phase !== 'restored' && (
        <div className="card" data-testid="km-power-offline">
          <h3 className="section-title" style={{ marginTop: 0 }}>
            {t('power.offline_title')}
          </h3>
          <ul className="plain-list">
            {p.offline.map((ref) => (
              <li key={ref} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0' }} data-ref={ref}>
                <span className="km-st bad">
                  <span aria-hidden>✕</span> {nameOf(ref)}
                </span>
                {inc.can_handle && p.phase === 'waiting' && (
                  <button type="button" className="btn btn-sm" disabled={busy !== null} onClick={() => void skip(ref)} aria-label={t('power.skip_aria', { name: nameOf(ref) })}>
                    {t('power.skip')}
                  </button>
                )}
              </li>
            ))}
          </ul>
          {p.phase === 'waiting' && <p className="muted km-sub">{t('power.escalate_note')}</p>}
          {note && (
            <p className="km-sub" role="status">
              {note}
            </p>
          )}
        </div>
      )}
      {p.events.length > 0 && (
        <details className="card" data-testid="km-power-events">
          <summary>
            <strong>{t('power.events_title')}</strong>
          </summary>
          <ul className="plain-list">
            {p.events.map((e) => (
              <li key={e.started_at}>
                {e.ended_at
                  ? t('power.event_line', {
                      from: timeStamp(e.started_at, undefined, false),
                      to: timeStamp(e.ended_at, undefined, false),
                      min: Math.max(1, Math.round((Date.parse(e.ended_at) - Date.parse(e.started_at)) / 60_000)),
                    })
                  : t('power.event_ongoing', { from: timeStamp(e.started_at, undefined, false) })}
                {e.escalated_at && <span className="muted"> · {t('power.escalated')}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** 最近 30 分鐘電池電量：一條線 + 端點標 %，下面〔看數字〕。 */
function BatteryLine({ points }: { points: { at: string; v: number }[] }) {
  const { t } = useTranslation(NS);
  const w = 320;
  const h = 64;
  const t0 = Date.parse(points[0].at);
  const t1 = Date.parse(points[points.length - 1].at);
  const x = (at: string) => (t1 > t0 ? ((Date.parse(at) - t0) / (t1 - t0)) * (w - 40) : 0);
  const y = (v: number) => h - 4 - ((h - 8) * v) / 100;
  const last = points[points.length - 1];
  const title = t('power.chart_title', { time: timeStamp(points[0].at, undefined, false) });
  return (
    <figure style={{ margin: 0 }}>
      <figcaption className="km-sub">{title}</figcaption>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={`${title}：${Math.floor(last.v)}%`} style={{ maxWidth: '100%' }}>
        <polyline points={points.map((p) => `${x(p.at).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ')} fill="none" stroke="currentColor" strokeWidth="2" />
        <text x={x(last.at) + 6} y={y(last.v) + 4} fontSize="13" fill="currentColor">
          {Math.floor(last.v)}%
        </text>
      </svg>
      <details>
        <summary className="km-sub">{t('power.show_numbers')}</summary>
        <table className="km-table">
          <thead>
            <tr>
              <th>{t('power.col_time')}</th>
              <th className="num">{t('power.col_battery')}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.at}>
                <td>{timeStamp(p.at, undefined, false)}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>
                  {Math.floor(p.v)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

/** 事故列的停電一行（清單與總覽）：從家戶的 UPS 摘要找這件事故的 UPS。 */
export function PowerRowLine({ inc }: { inc: Incident }) {
  // 只有進行中的停電事故才查（其他事故列不碰範圍與網路）
  if (!isPowerIncident(inc) || inc.now_state !== 'ongoing') return null;
  return <PowerRowLineInner inc={inc} />;
}

function PowerRowLineInner({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  const { homes, planes } = useScope();
  const api = homes.find((h) => h.home_id === inc.home_id)?.endpoints?.api ?? planes[0]?.api ?? '';
  const q = useQuery({
    queryKey: ['kmgr', 'power', 'home', inc.home_id],
    queryFn: () => powerApi.home(api, inc.home_id),
    staleTime: 30_000,
    retry: false,
  });
  if (!q.data) return null;
  const u = q.data.ups.find((x) => x.outage_incident_id === inc.incident_id) ?? q.data.ups.find((x) => x.probe_id === inc.target_ref);
  if (!u) return null;
  const min = runtimeMin(u.runtime_s);
  return (
    <div className="km-iline km-pwr-line" data-testid="km-power-row">
      {min === undefined
        ? t('power.row_line_unk')
        : u.load_pct !== undefined
          ? t('power.row_line', { n: min, load: Math.floor(u.load_pct) })
          : t('power.row_line_noload', { n: min })}
      {!u.downstream && (
        <span className="muted">
          {' · '}
          {t('power.no_downstream')}{' '}
          <Link to={upsHref(inc.home_id, u.probe_id)}>{t('power.register')}</Link>
        </span>
      )}
    </div>
  );
}
